/**
 * Smart Spine asset detector.
 *
 * Given a flat list of File objects (with optional webkitRelativePath from a
 * folder input or directory drop), classifies and groups them into complete
 * Spine asset bundles: one skeleton (.json/.skel) + one atlas (.atlas or
 * .atlas.txt) + one or more PNG texture pages.
 *
 * Misc Unity/project files (.meta, .asset, .mat, .controller, .fbx, …) are
 * silently ignored and reported in DetectionResult.unmatched.
 */

export interface SpineGroup {
  /** Display name — stem of the atlas or skeleton filename */
  name: string;
  skeletonFile: File;
  atlasFile: File;
  pngFiles: File[];
}

export interface DetectionResult {
  groups: SpineGroup[];
  /** Files that were not part of any detected Spine group (excluding .meta/.DS_Store etc.) */
  unmatched: File[];
}

// ── helpers ────────────────────────────────────────────────────────────────

/** Strip compound Spine extensions (.atlas.txt, .atlas, .json, .skel, .png, .txt) */
function fileStem(name: string): string {
  return name
    .replace(/\.atlas\.txt$/i, '')
    .replace(/\.atlas$/i, '')
    .replace(/\.(json|skel|png|txt)$/i, '');
}

/** Parent directory of a file, using webkitRelativePath if available */
function parentDir(f: File): string {
  const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
  if (!rel) return '';
  const parts = rel.split('/');
  return parts.slice(0, -1).join('/');
}

/** Silently ignored — Unity project clutter */
const IGNORED_EXTS = new Set([
  '.meta', '.asset', '.mat', '.controller', '.fbx', '.prefab',
  '.anim', '.overrideController', '.mixer', '.renderTexture', '.ds_store',
]);
function isIgnored(f: File): boolean {
  const low = f.name.toLowerCase();
  if (low === '.ds_store') return true;
  const dot = low.lastIndexOf('.');
  return dot !== -1 && IGNORED_EXTS.has(low.slice(dot));
}

function isAtlasByName(f: File): boolean {
  const low = f.name.toLowerCase();
  return low.endsWith('.atlas.txt') || low.endsWith('.atlas');
}

function isPngByName(f: File): boolean {
  return f.name.toLowerCase().endsWith('.png') || f.type === 'image/png';
}

/** Validate Spine JSON: must have skeleton.spine version field */
async function isSpineJson(f: File): Promise<boolean> {
  if (!f.name.toLowerCase().endsWith('.json')) return false;
  try {
    // Read only the first 1 KB — enough to find the skeleton key
    const slice = f.slice(0, 1024);
    const text = await slice.text();
    return text.includes('"skeleton"') && text.includes('"spine"');
  } catch {
    return false;
  }
}

/**
 * Validate Spine atlas: first non-blank, non-indented line must end with an
 * image extension; next non-blank line must start with "size".
 */
async function isSpineAtlas(f: File): Promise<boolean> {
  if (!isAtlasByName(f)) return false;
  try {
    const text = await f.slice(0, 256).text();
    const lines = text.split('\n').map((l) => l.trimEnd());
    const meaningful = lines.filter((l) => l.trim().length > 0);
    const first = meaningful[0]?.trim() ?? '';
    const second = meaningful[1]?.trim().toLowerCase() ?? '';
    return /\.(png|jpg|jpeg|webp)$/i.test(first) && second.startsWith('size');
  } catch {
    return false;
  }
}

/**
 * Parse all PNG page references from an atlas file.
 * Page filenames are non-indented, non-blank lines ending with an image ext.
 */
async function parseAtlasPngs(atlasFile: File): Promise<string[]> {
  const text = await atlasFile.text();
  const pngs: string[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trimEnd();
    if (!line || line !== line.trim()) continue; // blank or indented → skip
    if (/\.(png|jpg|jpeg|webp)$/i.test(line.trim())) {
      pngs.push(line.trim());
    }
  }
  return pngs;
}

/**
 * Pick the best display name for a Spine group.
 * Priority: atlas/skel stem → parent folder name → "skeleton"
 */
function resolveGroupName(primaryStem: string, atlasFile: File, skelFile: File): string {
  if (primaryStem && primaryStem !== 'skeleton') return primaryStem;
  // Try atlas stem
  const aStem = fileStem(atlasFile.name);
  if (aStem && aStem !== 'skeleton') return aStem;
  // Fall back to parent folder name (from webkitRelativePath)
  const rel = (skelFile as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
  if (rel) {
    const parts = rel.split('/');
    if (parts.length >= 2) return parts[parts.length - 2]; // immediate parent dir
  }
  return primaryStem || 'skeleton';
}

// ── main export ────────────────────────────────────────────────────────────

export async function detectSpineGroups(files: File[]): Promise<DetectionResult> {
  const all = Array.from(files);
  const used = new Set<File>();
  const groups: SpineGroup[] = [];

  // 1. Classify
  const skeletons: File[] = [];
  const atlases: File[] = [];
  const pngs: File[] = [];

  await Promise.all(
    all.map(async (f) => {
      if (isIgnored(f)) return;
      if (isPngByName(f))         { pngs.push(f); return; }
      if (f.name.toLowerCase().endsWith('.skel')) { skeletons.push(f); return; }
      if (await isSpineJson(f))   { skeletons.push(f); return; }
      if (await isSpineAtlas(f))  { atlases.push(f); return; }
      // else: unmatched (not ignored, not Spine)
    })
  );

  // Global PNG lookup by filename (basename)
  const pngByName = new Map<string, File>(pngs.map((p) => [p.name, p]));

  // 2. Does any file have a relative path? (webkitdirectory input or recursive drop)
  const hasRelPaths = all.some(
    (f) => !!(f as File & { webkitRelativePath?: string }).webkitRelativePath
  );

  // ── Strategy A: group by directory ──────────────────────────────────────
  if (hasRelPaths) {
    // Bucket files by parent directory
    const dirs = new Map<string, { skeletons: File[]; atlases: File[]; pngs: File[] }>();
    const bucket = (f: File) => {
      const dir = parentDir(f);
      if (!dirs.has(dir)) dirs.set(dir, { skeletons: [], atlases: [], pngs: [] });
      return dirs.get(dir)!;
    };
    for (const f of skeletons) bucket(f).skeletons.push(f);
    for (const f of atlases)   bucket(f).atlases.push(f);
    for (const f of pngs)      bucket(f).pngs.push(f);

    for (const [, b] of dirs) {
      if (!b.skeletons.length || !b.atlases.length) continue;

      // Prefer non-generic skeleton name
      const skeleton =
        b.skeletons.find((f) => fileStem(f.name) !== 'skeleton') ?? b.skeletons[0];
      const skelStem = fileStem(skeleton.name);

      // Prefer atlas matching skeleton stem; fall back to first
      const atlas =
        b.atlases.find((f) => fileStem(f.name) === skelStem) ?? b.atlases[0];

      // Resolve PNGs from atlas references, prioritising dir-local files
      const referencedNames = await parseAtlasPngs(atlas);
      const resolvedPngs: File[] = [];
      for (const pngName of referencedNames) {
        const local  = b.pngs.find((f) => f.name === pngName);
        const global = pngByName.get(pngName);
        const found  = local ?? global;
        if (found) resolvedPngs.push(found);
      }
      // Fallback: all PNGs in the same dir
      const finalPngs = resolvedPngs.length > 0 ? resolvedPngs : b.pngs;
      if (!finalPngs.length) continue;

      const name = resolveGroupName(skelStem, atlas, skeleton);
      groups.push({ name, skeletonFile: skeleton, atlasFile: atlas, pngFiles: finalPngs });
      used.add(skeleton);
      used.add(atlas);
      finalPngs.forEach((p) => used.add(p));
    }
  }

  // ── Strategy B: flat file drop — atlas-driven grouping ─────────────────
  else {
    for (const atlas of atlases) {
      const referencedNames = await parseAtlasPngs(atlas);
      const resolvedPngs = referencedNames
        .map((n) => pngByName.get(n))
        .filter((f): f is File => !!f && !used.has(f));
      if (!resolvedPngs.length) continue;

      const atlasStem = fileStem(atlas.name);
      const skeleton =
        skeletons.find((f) => fileStem(f.name) === atlasStem && !used.has(f)) ??
        skeletons.find((f) => !used.has(f));
      if (!skeleton) continue;

      const name = resolveGroupName(atlasStem, atlas, skeleton);
      groups.push({ name, skeletonFile: skeleton, atlasFile: atlas, pngFiles: resolvedPngs });
      used.add(skeleton);
      used.add(atlas);
      resolvedPngs.forEach((p) => used.add(p));
    }
  }

  // 3. Anything non-ignored and unused = unmatched
  const unmatched = all.filter((f) => !isIgnored(f) && !used.has(f));

  return { groups, unmatched };
}

// ── Folder / drag-drop file collection ────────────────────────────────────

/**
 * Collect all File objects from a DataTransfer, recursively traversing
 * dropped folders. Supports File System Access API (Chrome 86+) and the
 * legacy webkitGetAsEntry fallback.
 */
export async function collectDroppedFiles(transfer: DataTransfer): Promise<File[]> {
  const out: File[] = [];
  const items = Array.from(transfer.items).filter((i) => i.kind === 'file');

  await Promise.all(
    items.map(async (item) => {
      // Modern: File System Access API
      if ('getAsFileSystemHandle' in item) {
        try {
          const handle = await (
            item as DataTransferItem & {
              getAsFileSystemHandle(): Promise<FileSystemHandle>;
            }
          ).getAsFileSystemHandle();
          if (handle.kind === 'file') {
            out.push(await (handle as FileSystemFileHandle).getFile());
          } else {
            await walkFSADirectory(handle as FileSystemDirectoryHandle, out);
          }
          return;
        } catch {
          // fall through to legacy
        }
      }

      // Legacy: webkitGetAsEntry
      const entry = (item as DataTransferItem & { webkitGetAsEntry?(): FileSystemEntry | null })
        .webkitGetAsEntry?.();
      if (entry) {
        await walkLegacyEntry(entry, out);
      } else {
        const f = item.getAsFile();
        if (f) out.push(f);
      }
    })
  );

  return out;
}

async function walkFSADirectory(dir: FileSystemDirectoryHandle, out: File[]): Promise<void> {
  for await (const [, handle] of (dir as unknown as AsyncIterable<[string, FileSystemHandle]>)) {
    if (handle.kind === 'file') {
      out.push(await (handle as FileSystemFileHandle).getFile());
    } else {
      await walkFSADirectory(handle as FileSystemDirectoryHandle, out);
    }
  }
}

function walkLegacyEntry(entry: FileSystemEntry, out: File[]): Promise<void> {
  if (entry.isFile) {
    return new Promise((resolve) => {
      (entry as FileSystemFileEntry).file(
        (f) => { out.push(f); resolve(); },
        () => resolve()
      );
    });
  }
  if (entry.isDirectory) {
    return new Promise((resolve) => {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      const read = (entries: FileSystemEntry[]) => {
        if (!entries.length) { resolve(); return; }
        Promise.all(entries.map((e) => walkLegacyEntry(e, out))).then(() => {
          reader.readEntries(read, () => resolve());
        });
      };
      reader.readEntries(read, () => resolve());
    });
  }
  return Promise.resolve();
}
